import React, { useState } from 'react';
import { Button, MessageBarActions, MessageBarBody, MessageBarTitle, Spinner } from '@fluentui/react-components';
import { ArrowSync20Regular, History20Regular, Info20Regular, Warning20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { GuideDrawer, GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideBannerAction, GuideLinkButton } from '../../../theme/components/GuideButtons';
import { GuideHelpText } from '../../../theme/components/GuideField';
import { GuideKpiCard, GuideKpiGrid } from '../../../theme/components/GuideKpiCard';
import { GuideTooltip } from '../../../theme/components/GuideTooltip';
import { StatusPill } from '../../../theme/components/StatusPill';
import {
  formatDateTime,
  isSyncActive,
  syncScopeText,
  syncStatusLabelKey,
  syncStatusTone,
  syncTriggerLabelKey,
} from '../../../functions/ProviderConsumptions.Functions';
import { getApiErrorMessage } from '../../../functions/ProviderConsumptionImport.Functions';
import { ProviderSyncRun, ProviderSyncStatus } from '../../../models/Entities/ProviderConsumptions/ProviderConsumptions';
import { ErrorType } from '../../../models/HttpError';

export interface ProviderSyncCardProps {
  /** Última corrida; `null` = nunca se sincronizó; `undefined` = todavía no se leyó. */
  run: ProviderSyncRun | null | undefined;
  loading: boolean;
  error?: ErrorType;
  /** Se pidió una sincronización y había otra en curso (409). */
  conflict: boolean;
  /** Se está esperando el avance de una corrida recién pedida. */
  waiting: boolean;
  onSync: () => void;
  onRetry: () => void;
  onOpenHistory: () => void;
  onOpenAlarms: (run: ProviderSyncRun) => void;
}

const tr = (t: (key: string, options?: Record<string, unknown>) => string, path: string, options?: Record<string, unknown>) =>
  t(`providerConsumptions.sync.${path}`, options);

/** Drawer con el detalle completo de la corrida: fechas, alcance, contadores como KPI y alarmas. */
const ProviderSyncDetailDrawer = ({ run, open, onClose, onOpenAlarms }: { run: ProviderSyncRun; open: boolean; onClose: () => void; onOpenAlarms: (run: ProviderSyncRun) => void }) => {
  const { t } = useTranslation();
  const alarmCount = run.alarmCount ?? 0;
  const failed = run.status === ProviderSyncStatus.Fail || run.status === ProviderSyncStatus.PartiallyFailed;

  const fact = (label: string, value: React.ReactNode) => (
    <div className="pcs-fact">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );

  return (
    <GuideDrawer
      open={open}
      size="medium"
      eyebrow={tr(t, 'last')}
      title={tr(t, 'detailTitle')}
      subtitle={`${t(syncStatusLabelKey(run.status))} · ${t(syncTriggerLabelKey(run.trigger))}`}
      onDismiss={onClose}
      secondaryAction={{ label: t('common.close') }}
      testId="pcs-sync-detail-drawer"
    >
      {failed && (
        <GuideMessageBar intent={run.status === ProviderSyncStatus.Fail ? 'error' : 'warning'} layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>{run.status === ProviderSyncStatus.Fail ? tr(t, 'failedTitle') : tr(t, 'partialTitle')}</MessageBarTitle>
            {run.errorMessage?.trim() || tr(t, 'failedText')}
          </MessageBarBody>
        </GuideMessageBar>
      )}

      <GuideDrawerSection title={tr(t, 'detailRun')}>
        <dl className="pcs-facts">
          {fact(tr(t, 'scope'), syncScopeText(run, t))}
          {fact(tr(t, 'requestedAt'), formatDateTime(run.requestedAt))}
          {fact(tr(t, 'startedAt'), formatDateTime(run.startedAt))}
          {fact(tr(t, 'finishedAt'), formatDateTime(run.finishedAt))}
        </dl>
      </GuideDrawerSection>

      <GuideDrawerSection title={tr(t, 'detailResults')}>
        <GuideKpiGrid className="pcs-sync__kpis">
          <GuideKpiCard label={tr(t, 'candidates')} value={run.candidateCount ?? 0} />
          <GuideKpiCard label={tr(t, 'created')} value={run.createdCount ?? 0} tone="success" />
          <GuideKpiCard label={tr(t, 'updated')} value={run.updatedCount ?? 0} tone="info" />
          <GuideKpiCard label={tr(t, 'deleted')} value={run.deletedCount ?? 0} tone={(run.deletedCount ?? 0) > 0 ? 'warning' : 'neutral'} />
          <GuideKpiCard label={tr(t, 'protected')} value={run.protectedCount ?? 0} />
          <GuideKpiCard
            label={tr(t, 'alarms')}
            value={alarmCount}
            tone={alarmCount > 0 ? 'warning' : 'neutral'}
            detail={alarmCount > 0 ? <GuideLinkButton size="small" icon={<Warning20Regular />} onClick={() => onOpenAlarms(run)}>{tr(t, 'viewAlarms', { count: alarmCount })}</GuideLinkButton> : undefined}
          />
        </GuideKpiGrid>
      </GuideDrawerSection>
    </GuideDrawer>
  );
};

/**
 * Barra compacta "Última sincronización": en una línea el estado, el disparo, cuándo terminó y
 * los contadores clave; a la derecha "Detalle" (drawer con todo), "Historial" y "Sincronizar".
 * Los avisos (en curso, conflicto, fallo) van debajo. Muestra `errorMessage`, NUNCA `errorStackTrace`.
 */
export const ProviderSyncCard = ({ run, loading, error, conflict, waiting, onSync, onRetry, onOpenHistory, onOpenAlarms }: ProviderSyncCardProps) => {
  const { t } = useTranslation();
  const [detailOpen, setDetailOpen] = useState(false);
  const active = waiting || isSyncActive(run);
  const failed = !!run && (run.status === ProviderSyncStatus.Fail || run.status === ProviderSyncStatus.PartiallyFailed);
  const alarmCount = run?.alarmCount ?? 0;

  const stat = (label: string, value: number | null | undefined, testId?: string) => (
    <span className="pcs-sync__stat" title={label}>
      <span className="pcs-sync__stat-label">{label}</span>
      <span className="pcs-sync__stat-value ichtys-num" data-testid={testId}>{value ?? 0}</span>
    </span>
  );

  return (
    <section className="pcs-sync" aria-label={tr(t, 'title')} data-testid="pcs-sync-card">
      <div className="pcs-sync__bar" data-testid={run ? 'pcs-sync-run' : undefined}>
        <div className="pcs-sync__summary">
          <span className="pcs-sync__label">{tr(t, 'last')}</span>

          {run === undefined && loading && <span className="pcs-sync__muted" data-testid="pcs-sync-loading"><Spinner size="extra-tiny" /> {tr(t, 'loading')}</span>}
          {run === null && !waiting && <GuideHelpText data-testid="pcs-sync-never">{tr(t, 'never')}</GuideHelpText>}
          {run === null && waiting && <span className="pcs-sync__muted"><Spinner size="extra-tiny" /> {tr(t, 'syncing')}</span>}

          {run && (
            <>
              <StatusPill tone={syncStatusTone(run.status)} data-testid="pcs-sync-status">{t(syncStatusLabelKey(run.status))}</StatusPill>
              <span className="pcs-sync__muted" data-testid="pcs-sync-trigger">{t(syncTriggerLabelKey(run.trigger))}</span>
              {run.finishedAt && <span className="pcs-sync__muted pcs-sync__when">{formatDateTime(run.finishedAt)}</span>}
              <span className="pcs-sync__sep" aria-hidden="true" />
              <span className="pcs-sync__stat" data-testid="pcs-sync-scope">{syncScopeText(run, t)}</span>
              {stat(tr(t, 'created'), run.createdCount, 'pcs-sync-created')}
              {stat(tr(t, 'updated'), run.updatedCount)}
              {stat(tr(t, 'deleted'), run.deletedCount)}
              {alarmCount > 0 ? (
                <GuideLinkButton size="small" icon={<Warning20Regular />} onClick={() => onOpenAlarms(run)} data-testid="pcs-sync-alarms">
                  {tr(t, 'viewAlarms', { count: alarmCount })}
                </GuideLinkButton>
              ) : (
                <span className="pcs-sync__stat" data-testid="pcs-sync-alarm-count">
                  <span className="pcs-sync__stat-label">{tr(t, 'alarms')}</span>
                  <span className="pcs-sync__stat-value ichtys-num">0</span>
                </span>
              )}
            </>
          )}
        </div>

        <div className="pcs-sync__actions">
          {run && (
            <GuideTooltip content={tr(t, 'detail')} relationship="label">
              <Button appearance="secondary" icon={<Info20Regular />} onClick={() => setDetailOpen(true)} data-testid="pcs-open-detail">
                {tr(t, 'detail')}
              </Button>
            </GuideTooltip>
          )}
          <Button appearance="secondary" icon={<History20Regular />} onClick={onOpenHistory} data-testid="pcs-open-history">
            {tr(t, 'history')}
          </Button>
          <Button
            appearance="primary"
            icon={active ? <Spinner size="extra-tiny" /> : <ArrowSync20Regular />}
            disabled={active}
            aria-busy={active || undefined}
            onClick={onSync}
            data-testid="pcs-open-sync"
          >
            {active ? tr(t, 'syncing') : tr(t, 'sync')}
          </Button>
        </div>
      </div>

      {conflict && (
        <GuideMessageBar intent="info" layout="multiline" data-testid="pcs-sync-conflict">
          <MessageBarBody>
            <MessageBarTitle>{tr(t, 'conflictTitle')}</MessageBarTitle>
            {tr(t, 'conflictText')}
          </MessageBarBody>
        </GuideMessageBar>
      )}

      {error && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pcs-sync-error">
          <MessageBarBody>
            <MessageBarTitle>{tr(t, 'loadError')}</MessageBarTitle>
            {getApiErrorMessage(error)}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={onRetry}>{t('providerConsumptions.errors.retry')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}

      {active && (
        <GuideMessageBar intent="info" layout="multiline" data-testid="pcs-sync-active">
          <MessageBarBody>
            <MessageBarTitle>{tr(t, 'activeTitle')}</MessageBarTitle>
            {tr(t, 'activeText')}
          </MessageBarBody>
        </GuideMessageBar>
      )}

      {run && failed && (
        <GuideMessageBar intent={run.status === ProviderSyncStatus.Fail ? 'error' : 'warning'} layout="multiline" data-testid="pcs-sync-failed">
          <MessageBarBody>
            <MessageBarTitle>{run.status === ProviderSyncStatus.Fail ? tr(t, 'failedTitle') : tr(t, 'partialTitle')}</MessageBarTitle>
            {run.errorMessage?.trim() || tr(t, 'failedText')}
          </MessageBarBody>
        </GuideMessageBar>
      )}

      {run && <ProviderSyncDetailDrawer run={run} open={detailOpen} onClose={() => setDetailOpen(false)} onOpenAlarms={(current) => { setDetailOpen(false); onOpenAlarms(current); }} />}
    </section>
  );
};

export default ProviderSyncCard;
